document.addEventListener('DOMContentLoaded', () => {
  const config = {
    variantDataId: 'variant-gallery-data',

    productRootSelector: 'product-info',
    productMediaWrapperSelector: '.product__media-wrapper',

    sliderSelector: '.thumbnail-list',
    mainImageSelector: '.slider-mobile-gutter img',
    thumbnailSelector: 'button.thumbnail',

    variantIdSelector: 'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]',

    thumbnailListItemClass: 'thumbnail-list__item slider__slide',
    thumbnailButtonClass: 'thumbnail global-media-settings global-media-settings--no-shadow',

    activeThumbnailClass: 'active-thumbnail',

    nextButtonSelector: '.slider-button--next',
    prevButtonSelector: '.slider-button--prev',

    scrollAmount: 150,
    debounceDelay: 50,
    imageLoadTimeout: 5000,

    /*
      This delay lets Shopify/theme native variant JS update first.
      Your custom gallery then becomes the final visible state.
    */
    afterThemeUpdateDelay: 80,
  };

  const variantDataEl = document.getElementById(config.variantDataId);

  if (!variantDataEl) {
    console.error('Missing variant gallery data JSON.');
    return;
  }

  let variantData;

  try {
    variantData = JSON.parse(variantDataEl.textContent);
  } catch (error) {
    console.error('Failed to parse variant gallery data JSON:', error);
    return;
  }

  const productRoot = variantDataEl.closest(config.productRootSelector) || document;

  let slider = getSlider();
  let mainImage = getMainImage();

  if (!slider || !mainImage) {
    console.error('Missing slider container or main image element.');
    return;
  }

  let currentUpdateId = 0;
  let pendingImageLoad = null;

  function getSlider() {
    return productRoot.querySelector(config.sliderSelector) || document.querySelector(config.sliderSelector);
  }

  function getMainImage() {
    return productRoot.querySelector(config.mainImageSelector) || document.querySelector(config.mainImageSelector);
  }

  function setSliderEmptyState(isEmpty) {
    const sliderComponent = getSlider()?.closest('slider-component');
    sliderComponent?.classList.toggle('thumbnail-slider--empty', isEmpty);
  }

  function getMediaWrapper() {
    const image = getMainImage();

    return (
      image?.closest(config.productMediaWrapperSelector) ||
      productRoot.querySelector(config.productMediaWrapperSelector) ||
      productRoot ||
      document
    );
  }

  function normalizeUrl(url) {
    if (!url) return '';

    return url
      .replace(/^https?:/, '')
      .split('?')[0]
      .replace(/\/$/, '')
      .toLowerCase();
  }

  function ensureHttps(url) {
    if (!url) return '';

    return url.startsWith('//') ? `https:${url}` : url;
  }

  function addWidthParam(url, width) {
    const cleanUrl = ensureHttps(url);
    const separator = cleanUrl.includes('?') ? '&' : '?';

    return `${cleanUrl}${separator}width=${width}`;
  }

  function getSelectedVariantId() {
    const variantInput =
      productRoot.querySelector(config.variantIdSelector) || document.querySelector(config.variantIdSelector);

    return variantInput ? String(variantInput.value) : null;
  }

  function highlightActiveThumbnail() {
    mainImage = getMainImage();
    slider = getSlider();

    if (!mainImage || !slider || !mainImage.src) return;

    const cleanMainSrc = normalizeUrl(mainImage.src);
    const thumbnails = slider.querySelectorAll(config.thumbnailSelector);

    thumbnails.forEach((button) => {
      const img = button.querySelector('img');
      const fullSrc = button.getAttribute('data-full-src');

      const cleanThumbSrc = normalizeUrl(img?.src);
      const cleanFullSrc = normalizeUrl(fullSrc);

      const isActive = cleanMainSrc === cleanThumbSrc || cleanMainSrc === cleanFullSrc;

      button.classList.toggle(config.activeThumbnailClass, isActive);
    });
  }

  function loadImageWithTimeout(src, timeout = config.imageLoadTimeout) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      let timeoutId;

      const cleanup = () => {
        clearTimeout(timeoutId);
        img.onload = null;
        img.onerror = null;
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`Image load timeout: ${src}`));
      }, timeout);

      img.onload = () => {
        cleanup();
        resolve(img);
      };

      img.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load image: ${src}`));
      };

      img.src = src;
    });
  }

  async function updateMainImage(newSrc, updateId) {
    const finalSrc = ensureHttps(newSrc);

    if (!finalSrc) return;

    mainImage = getMainImage();

    if (!mainImage) return;

    if (pendingImageLoad) {
      pendingImageLoad.cancelled = true;
    }

    const loadPromise = {
      cancelled: false,
    };

    pendingImageLoad = loadPromise;

    mainImage.style.transition = 'opacity 0.15s ease-out';
    mainImage.style.opacity = '0';

    try {
      await loadImageWithTimeout(finalSrc);

      if (loadPromise.cancelled) return;
      if (updateId !== currentUpdateId) return;

      mainImage = getMainImage();

      if (!mainImage) return;

      const mediaWrapper = getMediaWrapper();

      /*
        Scope this to the current product media area only.
        Do not update every .global-media-settings image on the whole page.
      */
      mediaWrapper.querySelectorAll('img.global-media-settings').forEach((img) => {
        img.src = finalSrc;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      });

      mainImage.src = finalSrc;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');

      mainImage.style.transition = 'opacity 0.2s ease-in';
      mainImage.style.opacity = '1';

      window.setTimeout(() => {
        if (updateId === currentUpdateId) {
          highlightActiveThumbnail();
        }
      }, 250);
    } catch (error) {
      console.error('Failed to load image:', error);

      if (!loadPromise.cancelled && updateId === currentUpdateId && mainImage) {
        mainImage.style.opacity = '1';
      }
    } finally {
      if (pendingImageLoad === loadPromise) {
        pendingImageLoad = null;
      }
    }
  }

  function buildThumbnails(images) {
    slider = getSlider();

    if (!slider) {
      console.error('Slider not found.');
      return;
    }

    setSliderEmptyState(images.length === 0);

    const existingItems = Array.from(slider.children);

    images.forEach((imgSrc, index) => {
      const fullSrc = ensureHttps(imgSrc);
      const thumbUrl = addWidthParam(fullSrc, 200);

      if (index < existingItems.length) {
        const li = existingItems[index];
        let button = li.querySelector('button');
        let img = li.querySelector('img');

        li.className = config.thumbnailListItemClass;
        li.style.display = '';

        if (!button) {
          button = document.createElement('button');
          li.innerHTML = '';
          li.appendChild(button);
        }

        button.className = config.thumbnailButtonClass;
        button.type = 'button';
        button.setAttribute('aria-label', `View image ${index + 1}`);
        button.setAttribute('data-index', String(index));
        button.setAttribute('data-full-src', fullSrc);

        if (!img) {
          img = document.createElement('img');
          button.innerHTML = '';
          button.appendChild(img);
        }

        if (normalizeUrl(img.src) !== normalizeUrl(thumbUrl)) {
          img.src = thumbUrl;
        }

        img.width = 416;
        img.height = 416;
        img.alt = `Variant image ${index + 1}`;
        img.loading = 'lazy';
      } else {
        const li = document.createElement('li');
        li.className = config.thumbnailListItemClass;

        const button = document.createElement('button');
        button.className = config.thumbnailButtonClass;
        button.type = 'button';
        button.setAttribute('aria-label', `View image ${index + 1}`);
        button.setAttribute('data-index', String(index));
        button.setAttribute('data-full-src', fullSrc);

        const img = document.createElement('img');
        img.src = thumbUrl;
        img.width = 416;
        img.height = 416;
        img.alt = `Variant image ${index + 1}`;
        img.loading = 'lazy';

        button.appendChild(img);
        li.appendChild(button);
        slider.appendChild(li);
      }
    });

    while (slider.children.length > images.length) {
      slider.removeChild(slider.lastElementChild);
    }

    highlightActiveThumbnail();
  }

  async function updateGalleryByVariantId() {
    const updateId = ++currentUpdateId;
    const variantId = getSelectedVariantId();

    if (!variantId) return;

    const data = variantData[variantId];

    if (!data) {
      return;
    }

    const images = Array.isArray(data.images) ? data.images : [];

    if (!images.length) {
      if (updateId !== currentUpdateId) return;

      slider = getSlider();

      if (slider) slider.innerHTML = '';
      setSliderEmptyState(true);

      return;
    }

    if (updateId !== currentUpdateId) return;

    buildThumbnails(images);
    await updateMainImage(images[0], updateId);
  }

  function runAfterThemeVariantUpdate() {
    /*
      Shopify/theme JS can update selected variant/media after the change event.
      Two animation frames + a small timeout makes your custom gallery run last.
    */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(updateGalleryByVariantId, config.afterThemeUpdateDelay);
      });
    });
  }

  function debounce(func, wait = 100) {
    let timeout;

    return function debouncedFunction(...args) {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };
  }

  const debouncedVariantChange = debounce(runAfterThemeVariantUpdate, config.debounceDelay);

  document.body.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    const isVariantInput =
      target.matches('input[type="radio"]') ||
      target.matches('select') ||
      Array.from(target.classList).some((className) => className.endsWith('-input')) ||
      Boolean(target.getAttribute('name')?.includes('options[')) ||
      target.getAttribute('name') === 'id';

    if (isVariantInput) {
      debouncedVariantChange();
    }
  });

  /*
    Some Shopify themes also dispatch custom variant events.
    Listening to these makes the gallery more reliable across themes.
  */
  ['variant:change', 'variantChange', 'product:variant-change', 'product:variant:change'].forEach((eventName) => {
    document.addEventListener(eventName, debouncedVariantChange);
  });

  function onSliderClick(event) {
    const button = event.target.closest(config.thumbnailSelector);

    if (!button) return;

    event.preventDefault();

    const fullSrc = button.getAttribute('data-full-src');

    if (!fullSrc) return;

    updateMainImage(fullSrc, currentUpdateId);
  }

  function bindSliderClick() {
    const currentSlider = getSlider();

    if (!currentSlider) return;

    if (slider && slider !== currentSlider) {
      slider.removeEventListener('click', onSliderClick);
    }

    slider = currentSlider;
    slider.removeEventListener('click', onSliderClick);
    slider.addEventListener('click', onSliderClick);
  }

  bindSliderClick();

  const observer = new MutationObserver(() => {
    const currentSlider = getSlider();

    if (!currentSlider) return;

    if (currentSlider !== slider) {
      bindSliderClick();
      runAfterThemeVariantUpdate();
    }
  });

  observer.observe(productRoot === document ? document.body : productRoot, {
    childList: true,
    subtree: true,
  });

  document.body.addEventListener('click', (event) => {
    slider = getSlider();

    if (!slider) return;

    if (event.target.closest(config.nextButtonSelector)) {
      event.preventDefault();

      slider.scrollBy({
        left: config.scrollAmount,
        behavior: 'smooth',
      });
    }

    if (event.target.closest(config.prevButtonSelector)) {
      event.preventDefault();

      slider.scrollBy({
        left: -config.scrollAmount,
        behavior: 'smooth',
      });
    }
  });

  runAfterThemeVariantUpdate();
});
